import { IMPORT_TARGET_FIELDS, type MappingProposalEntry } from '@testflow/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipTooltip } from '../../components/tooltip.js';
import { type ImportSheet, importApi } from '../../lib/import-api.js';
import { translateImportError } from './import-errors.js';

// Design-Spec 3.8: Mapping-Tabelle mit drei Zeilenzuständen — bestätigt (✓ übernommen),
// KI-Vorschlag (gelber Tint + ✦-Chip + ✓/✕, Konfidenz), manuell ("nicht importieren ▾").
// #151 (Entscheidung 13.10.): Was die Tabelle anzeigt, ist das, was „Weiter“ speichert — ein
// offener KI-Vorschlag zählt dort als übernommen. Vorher fiel er still auf `ignore`, sodass in der
// Vorschau Spalten fehlten, die der Nutzer im Mapping gesehen hatte. Nicht still ist das trotzdem:
// gelber Tint, ✓/✕ und der Hinweis über „Weiter“ bleiben; ✕ ist der Weg, einen Vorschlag zu
// verwerfen (HITL, Design-Spec §5.2).

// Task-19-Review, Fund 1: Beispielzellen können lange Freitexte enthalten (Bemerkungen,
// Beschreibungen) und würden die Mapping-Tabelle sonst aufreißen. Im Repo existiert keine
// gemeinsame Kürzungs-Konvention für Anzeigewerte (weder eine Utility-Funktion noch ein CSS-Muster
// mit fester Spaltenbreite für Tabellenzellen wie diese) — deshalb eine eigene, benannte Konstante
// statt einer Zahl direkt im JSX.
const SAMPLE_VALUE_MAX_CHARS = 60;

function truncateSample(value: string): string {
  return value.length > SAMPLE_VALUE_MAX_CHARS
    ? `${value.slice(0, SAMPLE_VALUE_MAX_CHARS)}…`
    : value;
}

type RowState = 'proposed' | 'confirmed' | 'manual';

interface Row extends MappingProposalEntry {
  state: RowState;
}

function toRow(entry: MappingProposalEntry): Row {
  // Ein Profil-Treffer ist bestätigtes Menschenwissen → gilt sofort. Ein KI-Vorschlag wartet.
  // #125: dasselbe für ein aus dem Quell-Import übernommenes Mapping.
  if (entry.source === 'profile' || entry.source === 'reimport') {
    return { ...entry, state: 'confirmed' };
  }
  if (entry.source === 'ai') return { ...entry, state: 'proposed' };
  return { ...entry, state: 'manual', target_field: 'ignore' };
}

// #151 (Final-Review F2): Spalten mit offenem Vorschlag, dessen Feld auf demselben Blatt schon eine
// bestätigte oder manuelle Zeile trägt. Die Nutzerwahl schlägt den Vorschlag — würde er trotzdem
// gespeichert, gewänne im Entwurf still die spätere Spalte (build.ts project()), also womöglich
// gerade nicht die, die der Nutzer gewählt hat. Gilt je Blatt, weil jedes Blatt eigene Felder hat.
function overriddenProposals(rows: Row[]): Set<string> {
  const claimed = new Set(
    rows
      .filter((r) => r.state !== 'proposed' && r.target_field !== 'ignore')
      .map((r) => r.target_field),
  );
  return new Set(
    rows
      .filter(
        (r) => r.state === 'proposed' && r.target_field !== 'ignore' && claimed.has(r.target_field),
      )
      .map((r) => r.column),
  );
}

function SheetMapping({
  projectId,
  jobId,
  sheet,
  onRows,
}: {
  projectId: string;
  jobId: string;
  sheet: ImportSheet;
  onRows: (sheetId: string, rows: Row[]) => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [profile, setProfile] = useState<{ hit: boolean; columns: number }>({
    hit: false,
    columns: 0,
  });
  const [prefilledFrom, setPrefilledFrom] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Final-Review-Fund (Wichtig, HITL/NFR-5): KEIN Fallback auf detected_type. Task 16 hat genau
  // diesen Bypass schon einmal aus dem Backend entfernt (PUT .../mapping verlangt confirmed_type,
  // s. import.ts). confirmed_type ist NUR das, was ein Mensch im Klassifikations-Schritt
  // bestätigt hat — detected_type ist bloß der KI-Vorschlag. Ist confirmed_type (noch) null (z. B.
  // über einen künftigen Resume-/Deep-Link-Pfad, der den Wizard-State überspringt), darf dieses
  // Blatt NICHT automatisch als bestätigt an PATCH .../sheets/:sheetId gemeldet werden — sonst
  // würde der KI-Vorschlag serverseitig als menschliche Bestätigung durchgehen.
  const sheetType = sheet.confirmed_type;

  // onRows is a stable callback from the parent (useCallback) — see MappingStep below. Were it
  // recreated every render, adding it here would refetch the proposal in an endless loop (each
  // fetch's onRows(...) call triggers a parent re-render, which would hand this effect a new
  // "onRows" identity, re-running the effect, fetching again, forever).
  // `t` is deliberately NOT a dependency: it is only read inside the catch branch for error
  // formatting, and its identity is not something a re-fetch should ever key off. Including it
  // is actively unsafe here on top of being unnecessary — react-i18next's `t` is not guaranteed
  // stable across renders (see the test mock, which hands out a fresh function every render), so
  // treating it as a dependency risks the exact refetch loop described above.
  // biome-ignore lint/correctness/useExhaustiveDependencies: t intentionally excluded, see comment above.
  useEffect(() => {
    if (!sheetType) return; // kein Mensch hat bestätigt — keine PATCH-Bestätigung erzeugen.
    let cancelled = false;
    void (async () => {
      try {
        const proposal = await importApi.confirmSheetType(
          projectId,
          jobId,
          sheet.sheet_id,
          sheetType,
        );
        if (cancelled) return;
        const next = proposal.entries.map(toRow);
        setRows(next);
        setProfile({ hit: proposal.profile_hit, columns: proposal.profile_columns });
        setPrefilledFrom(proposal.prefilled_from ?? null);
        onRows(sheet.sheet_id, next);
      } catch (err) {
        if (!cancelled) {
          setError(translateImportError(t, err instanceof Error ? err.message : 'proposal_failed'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, jobId, sheet.sheet_id, sheetType, onRows]);

  if (!sheetType) {
    return (
      <div className="mb-6 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <p role="alert" className="text-xs text-status-fail">
          {t('import.mapping.notConfirmed', { sheet: sheet.sheet_name })}
        </p>
      </div>
    );
  }

  // Der EINE Schreibpfad für Row-Änderungen dieses Blatts — bündelt setRows (lokale Anzeige) und
  // onRows (meldet den neuen Stand an MappingStep.bySheet). update() und acceptAllProposals()
  // müssen beide hierüber gehen, sonst läuft der Wizard-State auseinander (#68).
  const commitRows = (next: Row[]): void => {
    setRows(next);
    onRows(sheet.sheet_id, next);
  };

  const update = (column: string, patch: Partial<Row>): void => {
    commitRows(rows.map((r) => (r.column === column ? { ...r, ...patch } : r)));
  };

  // #68: ~6 gemappte Spalten × ~16 Blätter wären ~90 Einzelklicks. Bewusst NUR blattweise — ein
  // "alle Blätter auf einmal" würde die HITL-Zusicherung aushöhlen (Leitprinzip 4). Rührt nur
  // Zeilen an, die noch 'proposed' sind; bereits manuell entschiedene ('manual') oder schon
  // bestätigte ('confirmed') Zeilen bleiben unangetastet.
  const acceptAllProposals = (): void => {
    commitRows(rows.map((r) => (r.state === 'proposed' ? { ...r, state: 'confirmed' } : r)));
  };

  const proposedCount = rows.filter((r) => r.state === 'proposed').length;
  const overridden = overriddenProposals(rows);

  return (
    <div className="mb-6 rounded-card border border-[var(--border)] bg-bg-surface">
      <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
        <span className="font-mono text-sm text-text-primary">{sheet.sheet_name}</span>
        <div className="flex items-center gap-2">
          {prefilledFrom && (
            <span className="rounded-chip px-2 py-1 text-[11px] text-status-pass">
              {t('import.mapping.prefillChip', { filename: prefilledFrom })}
            </span>
          )}
          {profile.hit && (
            <span className="rounded-chip px-2 py-1 text-[11px] text-status-pass">
              {t('import.mapping.profileChip', {
                columns: profile.columns,
                total: sheet.headers.length,
              })}
            </span>
          )}
          {proposedCount > 0 && (
            <button
              type="button"
              onClick={acceptAllProposals}
              className="rounded-btn border border-dashed border-[var(--border)] bg-[var(--tint-blue)] px-2 py-1 text-xs text-text-body"
            >
              ✦ {t('import.mapping.acceptAllProposals', { count: proposedCount })}
            </button>
          )}
        </div>
      </div>
      {error && (
        <p role="alert" className="px-4 py-3 text-xs text-status-fail">
          {error}
        </p>
      )}
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-text-muted">
            <th className="px-4 py-2">{t('import.mapping.column')}</th>
            <th className="px-4 py-2">{t('import.mapping.targetField')}</th>
            <th className="px-4 py-2">{t('import.mapping.example')}</th>
            <th className="px-4 py-2">{t('import.mapping.status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.column}
              className={r.state === 'proposed' ? 'bg-[var(--tint-yellow)]' : undefined}
            >
              <td className="px-4 py-2 font-mono text-text-body">{r.column}</td>
              <td className="px-4 py-2">
                <label htmlFor={`map-${sheet.sheet_id}-${r.column}`} className="sr-only">
                  {t('import.mapping.fieldFor', { column: r.column })}
                </label>
                <select
                  id={`map-${sheet.sheet_id}-${r.column}`}
                  aria-label={t('import.mapping.fieldFor', { column: r.column })}
                  value={r.target_field}
                  onChange={(e) =>
                    update(r.column, { target_field: e.target.value, state: 'manual' })
                  }
                  className="rounded-btn border border-[var(--border)] bg-bg-subtle px-2 py-1 text-xs text-text-body"
                >
                  {IMPORT_TARGET_FIELDS[sheetType].map((f) => (
                    <option key={f} value={f}>
                      {t(`import.fields.${f}`)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="max-w-[220px] px-4 py-2 text-text-muted">
                {/* #95: Referenzstelle aus dem Issue — natives title-Attribut (per Tastatur
                    unerreichbar) ersetzt durch ClipTooltip, damit app-weit EIN Muster gilt. Die
                    Blase traegt den VOLLEN Rohwert; die manuelle 60-Zeichen-Kuerzung
                    (truncateSample) bleibt zusaetzlich bestehen (verhindert extrem lange Werte im
                    DOM), CSS-truncate greift bei Bedarf zusaetzlich. Ohne Sample kein
                    fokussierbarer Tab-Stop noetig. */}
                {r.sample ? (
                  <ClipTooltip text={r.sample} className="block">
                    {/* biome-ignore lint/a11y/noNoninteractiveTabindex: reiner Lesetext ohne
                        Klickziel — der Tab-Stop existiert nur fuer die ClipTooltip-Blase (#95). */}
                    <span data-clip tabIndex={0} className="block truncate">
                      {truncateSample(r.sample)}
                    </span>
                  </ClipTooltip>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-4 py-2">
                {r.state === 'proposed' && (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="rounded-chip border border-dashed border-[var(--border)] bg-[var(--tint-blue)] px-2 py-1 text-[11px] text-text-body">
                      {t('import.mapping.proposalChip', {
                        confidence: Math.round(r.confidence * 100),
                      })}
                    </span>
                    <button
                      type="button"
                      aria-label={t('import.mapping.accept', { column: r.column })}
                      onClick={() => update(r.column, { state: 'confirmed' })}
                      className="text-status-pass"
                    >
                      ✓
                    </button>
                    <button
                      type="button"
                      aria-label={t('import.mapping.reject', { column: r.column })}
                      onClick={() => update(r.column, { state: 'manual', target_field: 'ignore' })}
                      className="text-status-fail"
                    >
                      ✕
                    </button>
                    {/* #151 (Final-Review F2): sichtbar machen, dass dieser Vorschlag beim „Weiter“
                        als „nicht importieren“ gespeichert wird — sonst stünde da ein Feld, das
                        die Vorschau nicht zeigt. */}
                    {overridden.has(r.column) && (
                      <span className="basis-full text-[11px] text-status-fail">
                        {t('import.mapping.proposalOverridden')}
                      </span>
                    )}
                  </span>
                )}
                {r.state === 'confirmed' && (
                  <span className="text-[11px] text-status-pass">
                    {t('import.mapping.confirmed')}
                  </span>
                )}
                {r.state === 'manual' && (
                  <span className="text-[11px] text-text-muted">{t('import.mapping.manual')}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MappingStep({
  projectId,
  jobId,
  sheets,
  onConfirmed,
}: {
  projectId: string;
  jobId: string;
  sheets: ImportSheet[];
  onConfirmed: () => void;
}) {
  const { t } = useTranslation();
  const [bySheet, setBySheet] = useState<Record<string, Row[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Stable identity across renders (see comment in SheetMapping's effect) — otherwise every
  // proposal fetch would trigger a re-fetch loop.
  const handleRows = useCallback((sheetId: string, rows: Row[]) => {
    setBySheet((prev) => ({ ...prev, [sheetId]: rows }));
  }, []);

  // Final-Review-Fund: kein Fallback auf detected_type hier ebenfalls — sonst würde ein noch
  // unbestätigtes Blatt (confirmed_type null) je nach KI-Vorschlag lautlos aus der Mapping-Liste
  // herausfallen oder hineinrutschen. Nur 'freeform' explizit BESTÄTIGT schließt ein Blatt aus;
  // ein unbestätigtes Blatt bleibt sichtbar (SheetMapping zeigt dann den Hinweis statt der
  // Tabelle, siehe dort) statt kommentarlos zu verschwinden.
  // Task 11 (#67): ein im Klassifikations-Schritt bewusst ausgeschlossenes Blatt ("Blatt
  // ignorieren") gehört unabhängig vom Typ nie in die Mapping-Liste — analog zum freeform-
  // Ausschluss, aber orthogonal dazu (build.ts/preview.ts filtern excluded serverseitig ebenso).
  const mappable = sheets.filter((s) => !s.excluded && s.confirmed_type !== 'freeform');
  if (mappable.length === 0) {
    return <div className="text-text-muted">{t('import.mapping.nothingToMap')}</div>;
  }

  // #151: Summe der noch offenen Vorschläge über alle Blätter — sie werden mit „Weiter“ übernommen.
  // Final-Review F2: ohne die, deren Feld schon vergeben ist — die werden gerade NICHT übernommen.
  const openProposals = mappable.reduce((sum, s) => {
    const rows = bySheet[s.sheet_id] ?? [];
    const overridden = overriddenProposals(rows);
    return sum + rows.filter((r) => r.state === 'proposed' && !overridden.has(r.column)).length;
  }, 0);

  async function handleContinue(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      for (const s of mappable) {
        const rows = bySheet[s.sheet_id] ?? [];
        // #151: Alle Zeilen tragen ihr angezeigtes target_field — auch ein noch offener Vorschlag
        // ('proposed'). Wer ihn nicht will, hat ✕ (→ 'ignore') oder wählt ein anderes Feld; der
        // Hinweis über „Weiter“ sagt das. Die frühere Regel (offen → 'ignore') speicherte etwas
        // anderes, als die Tabelle zeigte.
        // #151 (Final-Review F2): Ausnahme ist ein Vorschlag, dessen Feld der Nutzer auf diesem Blatt
        // schon einer anderen Spalte gegeben hat — Nutzerwahl schlägt Vorschlag, er wird 'ignore'
        // (die Zeile sagt das per Hinweis). Sonst gewänne im Entwurf still die spätere Spalte.
        const overridden = overriddenProposals(rows);
        const mappings = Object.fromEntries(
          rows.map((r) => [r.column, overridden.has(r.column) ? 'ignore' : r.target_field]),
        );
        await importApi.confirmMapping(projectId, jobId, s.sheet_id, mappings);
      }
      onConfirmed();
    } catch (err) {
      setError(translateImportError(t, err instanceof Error ? err.message : 'mapping_failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {mappable.map((s) => (
        <SheetMapping
          key={s.sheet_id}
          projectId={projectId}
          jobId={jobId}
          sheet={s}
          onRows={handleRows}
        />
      ))}
      {error && (
        <p role="alert" className="mb-3 text-xs text-status-fail">
          {error}
        </p>
      )}
      {openProposals > 0 && (
        <p className="mb-2 text-[11px] text-text-muted">
          {t('import.mapping.openProposalsHint', { count: openProposals })}
        </p>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => void handleContinue()}
        className="rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {t('import.mapping.continue')}
      </button>
    </div>
  );
}
